import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatBRL, formatDateTime } from "@/lib/api";
import { Link } from "react-router-dom";
import { UsersThree, CalendarBlank, TrendUp, Warning, ArrowRight, MapPin } from "@phosphor-icons/react";

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="tech-card p-6">
      <div className="flex items-start justify-between">
        <div className="label-tech text-muted-foreground">{label}</div>
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${accent === "primary" ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent"}`}>
          <Icon size={18} weight="bold" />
        </div>
      </div>
      <div className="mt-4 text-3xl md:text-4xl font-black tracking-tighter" style={{ fontFamily: "Manrope" }}>
        {value}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Layout
      title="Central de Operações"
      subtitle="Visão geral do seu negócio de inspeção aérea"
    >
      {loading ? (
        <div className="label-tech text-muted-foreground">Carregando dados...</div>
      ) : (
        <div className="space-y-8" data-testid="dashboard-content">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Clientes ativos" value={stats?.total_clients ?? 0} icon={UsersThree} accent="primary" />
            <StatCard label="Inspeções no mês" value={stats?.inspections_month ?? 0} icon={CalendarBlank} accent="accent" />
            <StatCard label="Receita do mês" value={formatBRL(stats?.revenue_month)} icon={TrendUp} accent="primary" />
            <StatCard label="Faturas pendentes" value={`${stats?.pending_invoices_count ?? 0} · ${formatBRL(stats?.pending_invoices_amount)}`} icon={Warning} accent="accent" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="tech-card p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="label-tech text-muted-foreground">Missões agendadas</div>
                  <h2 className="text-2xl font-extrabold tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>Próximas inspeções</h2>
                </div>
                <Link to="/inspections" className="label-tech text-accent flex items-center gap-1 hover:gap-2 transition-all" data-testid="link-all-inspections">
                  Ver todas <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
              {stats?.upcoming_inspections?.length ? (
                <div className="space-y-3">
                  {stats.upcoming_inspections.map((i) => (
                    <div key={i.id} className="flex items-center gap-4 p-3 border border-border rounded-md hover:border-primary/50 transition-colors">
                      <div className="w-12 h-12 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <MapPin size={18} weight="bold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{i.title}</div>
                        <div className="text-sm text-muted-foreground truncate">
                          {i.client_name} · {i.address_city || "Local não informado"}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="label-tech text-accent">{formatDateTime(i.scheduled_start)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 border border-dashed border-border rounded-md">
                  <div className="label-tech text-muted-foreground mb-2">Nenhuma inspeção agendada</div>
                  <Link to="/inspections" className="text-accent font-bold hover:underline">Agendar primeira inspeção →</Link>
                </div>
              )}

              {stats?.upcoming_site_visits?.length > 0 && (
                <>
                  <div className="mt-8 mb-4 flex items-center justify-between">
                    <div>
                      <div className="label-tech text-muted-foreground">Orçamento em campo</div>
                      <h2 className="text-xl font-extrabold tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>Próximas visitas técnicas</h2>
                    </div>
                    <div className="label-tech text-amber-400">◆ visita técnica</div>
                  </div>
                  <div className="space-y-3">
                    {stats.upcoming_site_visits.map((i) => (
                      <div key={i.id} className="flex items-center gap-4 p-3 border border-border rounded-md hover:border-amber-400/50 transition-colors">
                        <div className="w-12 h-12 rounded-md bg-amber-500/10 text-amber-400 flex items-center justify-center shrink-0">
                          <MapPin size={18} weight="bold" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold truncate">{i.title}</div>
                          <div className="text-sm text-muted-foreground truncate">
                            {i.client_name} · {i.address_city || "Local não informado"}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="label-tech text-amber-400">{formatDateTime(i.scheduled_start)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="tech-card p-6">
              <div className="label-tech text-muted-foreground">Atalhos rápidos</div>
              <h2 className="text-2xl font-extrabold tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>Próxima ação</h2>
              <div className="mt-6 space-y-2">
                <Link to="/clients" className="block p-3 border border-border rounded-md hover:border-primary hover:bg-secondary/50 transition-colors" data-testid="quick-new-client">
                  <div className="font-bold">+ Cadastrar cliente</div>
                  <div className="text-xs text-muted-foreground mt-1">Adicione um novo contratante</div>
                </Link>
                <Link to="/inspections" className="block p-3 border border-border rounded-md hover:border-primary hover:bg-secondary/50 transition-colors" data-testid="quick-new-inspection">
                  <div className="font-bold">+ Agendar inspeção</div>
                  <div className="text-xs text-muted-foreground mt-1">Voo, endereço, cliente</div>
                </Link>
                <Link to="/inspections" className="block p-3 border border-border rounded-md hover:border-amber-400 hover:bg-secondary/50 transition-colors" data-testid="quick-new-visit">
                  <div className="font-bold text-amber-400">◆ Visita técnica</div>
                  <div className="text-xs text-muted-foreground mt-1">Orçamento em campo antes do voo</div>
                </Link>
                <Link to="/proposals" className="block p-3 border border-border rounded-md hover:border-primary hover:bg-secondary/50 transition-colors" data-testid="quick-new-proposal">
                  <div className="font-bold">+ Gerar proposta</div>
                  <div className="text-xs text-muted-foreground mt-1">Modelo pronto em segundos</div>
                </Link>
                <Link to="/invoices" className="block p-3 border border-border rounded-md hover:border-primary hover:bg-secondary/50 transition-colors" data-testid="quick-new-invoice">
                  <div className="font-bold">+ Nova fatura</div>
                  <div className="text-xs text-muted-foreground mt-1">Registro de cobrança</div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
